/**
 * Widget Vue d'ensemble Locataire - VERSION FONCTIONNELLE
 * Avec actions directes et fonctionnalités utiles
 */

import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Event as EventIcon,
  CheckCircle as CompletedIcon,
  AttachMoney as MoneyIcon,
  FlightTakeoff as TripIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  Favorite as FavoriteIcon,
  Add as AddIcon,
  History as HistoryIcon,
  Assessment as StatsIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { DashboardWidget, TrendIndicator } from '../DashboardBase';
import { formatCurrency, PERIOD_LABELS } from '../../../constants/dashboardConstants';

const RenterOverviewWidget = ({ data, loading, period, onRefresh }) => {
  const navigate = useNavigate();
  
  console.log('🔥 RENTER WIDGET - data reçue:', data, 'loading:', loading);
  
  if (!data && !loading) {
    return (
      <DashboardWidget
        title="Vue d'ensemble"
        loading={loading}
        onRefresh={onRefresh}
      >
        <Typography color="text.secondary">
          Aucune donnée disponible
        </Typography>
      </DashboardWidget>
    );
  }

  const {
    active_reservations = 0,
    completed_reservations = 0,
    // total_spent et upcoming_trips supprimés
    trends = {}
  } = data || {};

  // Actions rapides fonctionnelles
  const quickActions = [
    {
      label: 'Rechercher du matériel',
      icon: <SearchIcon />,
      color: 'primary',
      action: () => navigate('/search'),
      description: 'Trouvez le matériel dont vous avez besoin'
    },
    {
      label: 'Mes réservations',
      icon: <FavoriteIcon />,
      color: 'error',
      action: () => navigate('/reservations-hub'),
      description: 'Toutes vos réservations de matériel'
    },
    {
      label: 'Parcourir les catégories',
      icon: <AddIcon />,
      color: 'success',
      action: () => navigate('/categories'),
      description: 'Explorez par catégories'
    },
    {
      label: 'Mon profil',
      icon: <HistoryIcon />,
      color: 'info',
      action: () => navigate('/profile'),
      description: 'Gérer mes informations'
    }
  ];

  return (
    <DashboardWidget
      title={`Mon activité - ${PERIOD_LABELS[period]}`}
      loading={loading}
      onRefresh={onRefresh}
      height="auto"
    >
      <Grid container spacing={3}>
        {/* Résumé avec actions directes */}
        <Grid item xs={12}>
          <Card sx={{ mb: 3, bgcolor: 'primary.50', border: 1, borderColor: 'primary.200' }}>
            <CardContent>
              <Typography variant="h6" color="primary.main" gutterBottom>
                📊 Votre situation actuelle
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary.main" fontWeight="bold">
                      {active_reservations}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Réservations actives
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="success.main" fontWeight="bold">
                      {completed_reservations}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Réservations terminées
                    </Typography>
                  </Box>
                </Grid>
                {/* Métriques total_spent et upcoming_trips supprimées */}
              </Grid>

              {/* Actions directes pour vos réservations */}
              {active_reservations > 0 && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'white', borderRadius: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    🎯 Actions sur vos réservations actives
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    <Button
                      variant="contained"
                      startIcon={<ViewIcon />}
                      onClick={() => navigate('/reservations-hub')}
                      size="small"
                    >
                      Voir mes réservations
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => navigate('/reservations-hub')}
                      size="small"
                    >
                      Gérer mes réservations
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<SearchIcon />}
                      onClick={() => navigate('/search')}
                      size="small"
                    >
                      Nouvelle recherche
                    </Button>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Actions rapides */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            🚀 Actions rapides
          </Typography>
          <Grid container spacing={2}>
            {quickActions.map((action, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card 
                  sx={{ 
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                  onClick={action.action}
                >
                  <CardContent sx={{ textAlign: 'center', p: 2 }}>
                    <Box
                      sx={{
                        color: `${action.color}.main`,
                        mb: 1,
                        fontSize: '2rem'
                      }}
                    >
                      {action.icon}
                    </Box>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {action.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {action.description}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'center', pt: 0 }}>
                    <Button 
                      size="small" 
                      color={action.color}
                      onClick={(e) => {
                        e.stopPropagation();
                        action.action();
                      }}
                    >
                      Accéder
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Informations utiles */}
        {(active_reservations > 0 || completed_reservations > 0) && (
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                💡 Conseils personnalisés
              </Typography>
              {active_reservations > 0 && (
                <Typography variant="body2" color="info.main" sx={{ mb: 1 }}>
                  • Vous avez {active_reservations} réservation(s) en cours. N'oubliez pas de vérifier les dates et conditions.
                </Typography>
              )}
              {completed_reservations > 0 && (
                <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
                  • Bravo ! Vous avez déjà complété {completed_reservations} réservation(s). 
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                • Ajoutez du matériel en favoris pour le retrouver facilement.
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>
    </DashboardWidget>
  );
};

export default RenterOverviewWidget;
